import {
  Button,
  Collapse,
  Input,
  InputAmount,
  InputDay,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  type RequestBody,
  showApiErrorNotification,
  useApiClient,
  useEnabledFeatures,
  useInvalidateApiQueries,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import {
  showSuccessNotification,
  showWarnNotification,
} from '@vrplatform/theme';
import {
  type Maybe,
  bankersRoundToCents,
  day,
  ensure,
  formatCurrency,
} from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  Loader,
  Collapse as MantineCollapse,
  Modal,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import type {
  activeStatus_enum,
  bankAccount_bool_exp,
  currency_enum,
} from '@vrplatform/graphql';
import { type ComponentProps, useEffect, useState } from 'react';
import {
  Controller,
  type UseFormReturn,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { buildBankingInitRequestBody } from '../../bankingInit.utils';

export type ConfirmBankConnectionsData = {
  connectionIds: Maybe<string[]>;
  bankAccountIds?: Maybe<string[]>;
};

export const STORYBOOK_CONFIRM_BANK_CONNECTIONS_QUERY_KEY =
  'reconciliation-confirm-bank-connections-modal';

type InsertAccountData = RequestBody<'post:/accounts'>;

type FormInputs = {
  bankAccounts: {
    id: string;
    title: string;
    accountId: string | null;
    currency: currency_enum | undefined;
    category: NonNullable<
      NonNullable<InsertAccountData['banking']>['category']
    >;
    type: NonNullable<NonNullable<InsertAccountData['banking']>['type']>;
    lastDigits: string | undefined;
    startDate: Date | null;
    balance: number | null;
    status: activeStatus_enum; // define whether should import or not
  }[];
};

const STATUSES = [
  {
    value: ensure<FormInputs['bankAccounts'][number]['status']>('active'),
    label: 'Yes, import',
  },
  {
    value: ensure<FormInputs['bankAccounts'][number]['status']>('inactive'),
    label: 'No, ignore',
  },
];

const CATEGORIES = [
  {
    value: ensure<FormInputs['bankAccounts'][number]['category']>('trust'),
    label: 'Yes',
  },
  {
    value: ensure<FormInputs['bankAccounts'][number]['category']>('operating'),
    label: 'No',
  },
];

function useBankAccountsSubscription(data: ConfirmBankConnectionsData | null) {
  const [{ id: teamId, statementStartAt }] = useTeam();

  const skip = !data?.connectionIds?.length && !data?.bankAccountIds?.length;

  const queryData = useQuery(
    (q, args) => {
      const connectionIds = args.data?.connectionIds;
      const bankAccountIds = args.data?.bankAccountIds;

      if (
        !teamId ||
        (!args.data?.connectionIds?.length &&
          !args.data?.bankAccountIds?.length)
      )
        return null;

      const _or: bankAccount_bool_exp[] = [];

      if (connectionIds?.length) {
        _or.push({
          connectionId: { _in: connectionIds },
        });
      }

      if (bankAccountIds?.length) {
        _or.push({
          id: { _in: bankAccountIds },
        });
      }

      const bankAccounts = q
        .bankAccounts({
          where: {
            tenantId: { _eq: args.teamId },
            _or,
          },
          order_by: [
            {
              name: 'asc_nulls_last',
            },
          ],
        })
        .map<FormInputs['bankAccounts'][number]>((bankAccount) => ({
          id: bankAccount.id,
          title: bankAccount.name || '-',
          accountId: bankAccount.accountId ?? null,
          category: bankAccount.category ?? 'trust',
          type: bankAccount.type ?? 'deposit',
          lastDigits: bankAccount.lastDigits,
          currency: bankAccount.currency,
          balance:
            bankAccount
              .bankRecords({
                limit: 1,
                order_by: [
                  {
                    date: 'desc_nulls_last',
                  },
                ],
              })
              .map((x) => x.centBalance ?? 0)[0] || 0,
          startDate: args.statementStartAt
            ? day(args.statementStartAt).toDate()
            : day().toDate(),
          status: 'active',
        }));

      const hasOtherCoaAccounts = !!q
        .accounts({
          limit: 1,
          where: {
            tenantId: { _eq: args.teamId },
            status: { _eq: 'active' },
            type: { _eq: 'bank' },
            _not: {
              bankingConnections: {},
            },
          },
        })
        .map((x) => x.id)[0];

      return {
        bankAccounts,
        canSelectCoaAccount: !!connectionIds?.length || hasOtherCoaAccounts,
      };
    },
    {
      queryKey: ['accounts', 'confirm-bankConnection-modal'],
      storybookQueryKey: STORYBOOK_CONFIRM_BANK_CONNECTIONS_QUERY_KEY,
      skip,
      keepPreviousData: true,
      variables: {
        teamId,
        data,
        statementStartAt,
      },
    }
  );

  const isCompleted = !!queryData.data?.bankAccounts?.[0]?.id;

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isCompleted && !skip) {
        queryData.refetch();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isCompleted, queryData, skip]);

  return queryData;
}

export const ConfirmBankConnectionsModal = ({
  closeModal: cl,
  data: input,
}: {
  closeModal: () => void;
  data: ConfirmBankConnectionsData | null;
}) => {
  const { OPEX } = useEnabledFeatures();

  const [activeAccount, setActiveAccount] = useState<number>(0);

  const { data: queryData, isFetching } = useBankAccountsSubscription(input);

  const bankAccounts = queryData?.bankAccounts || [];

  const methods = useForm<FormInputs>({
    values: {
      bankAccounts: bankAccounts,
    },
  });

  const { fields } = useFieldArray({
    control: methods.control,
    name: 'bankAccounts',
    keyName: '_id',
  });

  const invalidate = useInvalidateApiQueries(['bank-records', 'accounts']);

  const closeModal = () => {
    cl();
    methods.reset({
      bankAccounts: bankAccounts,
    });
    invalidate();
    setActiveAccount(0);
  };

  const isMultipleAccounts = bankAccounts.length > 1;

  const $api = useApiClient();

  const submit = async (data: FormInputs) => {
    if (data.bankAccounts.some((x) => !x.id)) {
      return showWarnNotification({
        title: 'Missing bank account ID',
        message: 'Please reach out to support if the issue persists.',
      });
    }

    const getAccountBody = (
      account: FormInputs['bankAccounts'][number]
    ): InsertAccountData => ({
      name: account.title,
      banking: {
        type: account.type ?? 'deposit',
        category: account.category ?? 'trust',
        last4: account.lastDigits,
      },
      status: account.status,
      type: 'bank',
    });

    try {
      const updatedAccounts = await Promise.all([
        ...data.bankAccounts.map(
          async (bankAccount): Promise<{ accountId: string | undefined }> => {
            if (bankAccount.status === 'inactive') {
              await $api
                .PUT('/bank-accounts/{id}', {
                  params: {
                    path: {
                      id: bankAccount.id,
                    },
                  },
                  body: {
                    status: 'inactive',
                    accountId: null,
                  },
                })
                .then((result) => {
                  if (result.error) throw result.error;
                  return result.data;
                });

              return {
                accountId: undefined,
              };
            }

            const updateBankAccount = async (accId: string) =>
              $api
                .PUT('/bank-accounts/{id}', {
                  params: {
                    path: {
                      id: bankAccount.id,
                    },
                  },
                  body: {
                    accountId: accId,
                  },
                })
                .then((result) => {
                  if (result.error) throw result.error;
                  return result.data;
                });

            if (bankAccount.accountId) {
              await Promise.all([
                updateBankAccount(bankAccount.accountId),
                $api
                  .PUT('/accounts/{id}', {
                    params: {
                      path: {
                        id: bankAccount.accountId,
                      },
                    },
                    body: getAccountBody(bankAccount),
                  })
                  .then((d) => {
                    if (d.error) throw d.error;
                    return d.data;
                  }),
              ]);

              return {
                accountId: bankAccount.accountId,
              };
            }

            const account = await $api
              .POST('/accounts', {
                body: getAccountBody(bankAccount),
              })
              .then((d) => {
                if (d.error) throw d.error;
                return d.data;
              });

            await updateBankAccount(account.id);

            return {
              accountId: account.id,
            };
          }
        ),
      ]);

      await Promise.all([
        updatedAccounts.map(({ accountId }, index) => {
          const formInput = data.bankAccounts[index];

          if (
            formInput.status === 'inactive' ||
            !accountId ||
            (!OPEX ? formInput.category !== 'trust' : false)
          )
            return null;

          return $api
            .POST('/accounts/{id}/banking-init', {
              params: {
                path: {
                  id: accountId,
                },
              },
              body: buildBankingInitRequestBody({
                account: {
                  banking: {
                    type: formInput.type,
                  },
                },
                startingBalanceAmount: formInput.balance ?? 0,
                startingBalanceDate: day(formInput.startDate).yyyymmdd(),
              }),
            })
            .then((d) => {
              if (d.error) throw d.error;
              return d.data;
            });
        }),
      ]);

      showSuccessNotification({
        title: 'Success!',
        message: 'Your trust accounts were successfully added.',
      });

      invalidate();
      closeModal();
    } catch (error) {
      showApiErrorNotification({
        error,
        title: 'Failed to update trust account',
        defaultMessage:
          'We failed to update the trust account. Please try again and contact support if the issue persists.',
      });
    }
  };

  const limit = 5;

  return (
    <Modal
      centered
      radius="md"
      opened={!!input}
      onClose={closeModal}
      closeOnClickOutside={!(isFetching || input?.connectionIds?.length)}
      withCloseButton={false}
      title={`Configure your trust account${isMultipleAccounts ? 's' : ''}`}
      size={550}
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      <Box
        component="form"
        onReset={closeModal}
        onSubmit={methods.handleSubmit(submit)}
      >
        {/* Return all bank accounts that were added an allow to name each of them */}
        <Box pos="relative" mb="xl">
          {!fields.length || isFetching ? (
            <Center
              pos="relative"
              sx={{
                minHeight: 250,
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Loader type="dots" c="gray" />

              <Text mb="xs" mt="sm">
                Currently fetching bank bank accounts...
              </Text>
              <Text size="xs" c="gray">
                This can take a minute. Please don't close the page.
              </Text>
            </Center>
          ) : fields.length && fields.length > 5 ? (
            fields.map((bankAccount, index) => {
              if (index !== activeAccount) return null;

              return (
                <Inputs
                  key={bankAccount.id}
                  index={index}
                  methods={methods}
                  bankAccount={bankAccount}
                  canSelectCoaAccount={!!queryData?.canSelectCoaAccount}
                />
              );
            })
          ) : (
            <Stack>
              {fields.map((bankAccount, index) => {
                return (
                  <Collapse
                    key={bankAccount.id}
                    minHeight={30}
                    title={
                      <Text fw={500}>
                        Configuration for {bankAccount.title}
                      </Text>
                    }
                    opened={index === activeAccount}
                    toggle={() => setActiveAccount(index)}
                    hideCollapse={fields.length === 1}
                    spacing={0}
                  >
                    <Inputs
                      index={index}
                      methods={methods}
                      bankAccount={bankAccount}
                      canSelectCoaAccount={!!queryData?.canSelectCoaAccount}
                    />
                  </Collapse>
                );
              })}
            </Stack>
          )}
        </Box>

        {!!fields.length && !isFetching && (
          <Group justify="space-between">
            {fields.length > limit ? (
              <Text c="gray" size="xs" ta="center" ml="xs">
                {activeAccount + 1} of {fields.length}
              </Text>
            ) : (
              <div />
            )}

            <Group>
              <Button type="reset" disabled={methods.formState.isSubmitting}>
                Cancel
              </Button>
              {activeAccount + 1 === fields.length ? (
                <Button
                  disabled={!fields.length}
                  key={'submit-button'}
                  type="submit"
                  variant="primary"
                  loading={methods.formState.isSubmitting}
                >
                  Submit
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    methods
                      .trigger(`bankAccounts.${activeAccount}`)
                      .then(() => {
                        const errors =
                          methods.formState.errors.bankAccounts?.[
                            activeAccount
                          ];

                        if (!errors) {
                          setActiveAccount((e) => e + 1);
                        }
                      });
                  }}
                  disabled={!fields.length}
                >
                  Continue
                </Button>
              )}
            </Group>
          </Group>
        )}
      </Box>
    </Modal>
  );
};

const Inputs = ({
  index,
  methods,
  bankAccount,
  canSelectCoaAccount,
}: {
  index: number;
  methods: UseFormReturn<FormInputs>;
  bankAccount: FormInputs['bankAccounts'][number];
  canSelectCoaAccount: boolean;
}) => {
  const [{ statementStartAt }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  const [coaType, setCoaType] =
    useState<ComponentProps<typeof AccountSelect>['coaType']>('create');

  // const isTrust =
  //   useWatch({
  //     control: methods.control,
  //     name: `bankAccounts.${index}.bankingCategory`,
  //     defaultValue: 'trust',
  //   }) === 'trust';

  const startDate =
    useWatch({
      control: methods.control,
      name: `bankAccounts.${index}.startDate`,
    }) ?? day().toDate();

  const accountId = useWatch({
    control: methods.control,
    name: `bankAccounts.${index}.accountId`,
  });

  const disabled =
    useWatch({
      control: methods.control,
      name: `bankAccounts.${index}.status`,
      defaultValue: 'active',
    }) === 'inactive';

  const disableInitBalance =
    disabled || (!!accountId && accountId !== bankAccount.accountId);

  return (
    <Stack>
      {/* Import */}
      <Controller
        control={methods.control}
        name={`bankAccounts.${index}.status`}
        rules={{
          required: 'Please select whether you want to reconcile this account',
        }}
        render={({ field, fieldState }) => {
          return (
            <InputWrapper
              label={'Would you like to import this account?'}
              error={fieldState.error?.message}
              sx={{
                flexDirection: 'column',
                display: 'flex',
              }}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            >
              <SegmentedControl
                data={STATUSES}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v);
                  methods.clearErrors(`bankAccounts.${index}.title`);
                  methods.clearErrors(`bankAccounts.${index}.startDate`);
                  methods.clearErrors(`bankAccounts.${index}.balance`);
                  methods.setValue(`bankAccounts.${index}.startDate`, null);
                  methods.setValue(`bankAccounts.${index}.balance`, null);
                }}
                onBlur={field.onBlur}
              />
            </InputWrapper>
          );
        }}
      />

      {/* Name */}
      <Controller
        control={methods.control}
        name={`bankAccounts.${index}.title`}
        defaultValue=""
        rules={{
          validate: (value) => value.trim().length > 0 || 'Please enter a name',
        }}
        render={({ field, fieldState }) => (
          <InputWrapper
            label={'Account name'}
            error={fieldState.error?.message}
          >
            <Input
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              placeholder={'Account name'}
              disabled={disabled}
            />
          </InputWrapper>
        )}
      />

      {OPEX && (
        <Controller
          control={methods.control}
          name={`bankAccounts.${index}.category`}
          rules={{
            required: 'Please select a category',
          }}
          render={({ field, fieldState }) => {
            return (
              <InputWrapper
                label={'Is this a trust account?'}
                description={
                  'Define if this is a trust account or an operating account for your business'
                }
                error={fieldState.error?.message}
                sx={{
                  flexDirection: 'column',
                  display: 'flex',
                }}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
              >
                <SegmentedControl
                  data={CATEGORIES}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                  }}
                  onBlur={field.onBlur}
                  disabled={disabled}
                />
              </InputWrapper>
            );
          }}
        />
      )}

      {canSelectCoaAccount && (
        <AccountSelect
          index={index}
          methods={methods}
          disabled={disabled}
          coaType={coaType}
          setCoaType={setCoaType}
        />
      )}

      <MantineCollapse
        in={
          !canSelectCoaAccount || (canSelectCoaAccount && coaType === 'create')
        }
      >
        <Controller
          control={methods.control}
          name={`bankAccounts.${index}.startDate`}
          rules={{
            validate: (value) => {
              if (disableInitBalance) return true;
              if (!value) return 'Please select a start date';

              if (day(value).isBefore(statementStartAt, 'day')) {
                return `Start date must be after your team's start date: ${day(statementStartAt).format('ll')}`;
              }

              return true;
            },
          }}
          render={({ field: { ref: _, ...field }, fieldState }) => {
            return (
              <InputWrapper
                label={'Bank Feed Start Date'}
                error={fieldState.error?.message}
                sx={{
                  flexDirection: 'column',
                  display: 'flex',
                }}
                mb="md"
              >
                <InputDay
                  {...field}
                  placeholder={day(new Date()).format('LL')}
                  error={!!fieldState.error}
                  onChange={(e) => {
                    field.onChange(e);
                    methods.clearErrors(`bankAccounts.${index}.startDate`);
                  }}
                  minDate={
                    statementStartAt
                      ? day(statementStartAt).toDate()
                      : undefined
                  }
                  disabled={disableInitBalance}
                />
              </InputWrapper>
            );
          }}
        />
        <Controller
          control={methods.control}
          name={`bankAccounts.${index}.balance`}
          rules={{
            required: disableInitBalance ? false : 'Please enter a balance',
          }}
          render={({ field: { ref: _, ...field }, fieldState }) => {
            return (
              <InputWrapper
                label={`Balance as of ${day(startDate).subtract(1, 'day').format('ll')}`}
                sx={{
                  flexDirection: 'column',
                  display: 'flex',
                }}
                error={fieldState.error?.message}
              >
                <InputAmount
                  value={
                    typeof field.value === 'number'
                      ? field.value / 100
                      : (field.value ?? '')
                  }
                  setValue={(value) => {
                    if (typeof value === 'string') {
                      if (value === '') {
                        field.onChange('');
                        return;
                      }

                      const bankers = bankersRoundToCents(value);
                      if (bankers === field.value) return;
                    }
                    field.onChange(bankersRoundToCents(value));
                  }}
                  error={!!fieldState.error}
                  decimalScale={2}
                  disabled={disableInitBalance}
                  placeholder={formatCurrency(0, bankAccount?.currency)}
                />
              </InputWrapper>
            );
          }}
        />
      </MantineCollapse>
    </Stack>
  );
};

function useAccountQuery() {
  const [{ id: teamId }] = useTeam();

  return useQuery(
    (q, args) => {
      return q
        .accounts({
          order_by: [
            {
              title: 'asc_nulls_last',
            },
          ],
          where: {
            tenantId: { _eq: args.teamId },
            status: { _eq: 'active' },
            type: { _eq: 'bank' },
            _not: {
              bankingConnections: {},
            },
          },
        })
        .map<SelectItem>((x) => ({
          value: x.id,
          label: x.title || '-',
        }));
    },
    {
      variables: {
        teamId,
      },
      keepPreviousData: true,
    }
  );
}

const AccountSelect = ({
  index,
  methods,
  disabled: disabledForm,
  coaType,
  setCoaType,
}: {
  methods: UseFormReturn<FormInputs>;
  index: number;
  disabled: boolean;
  coaType: 'create' | 'select';
  setCoaType: (v: 'create' | 'select') => void;
}) => {
  const { data: accounts = [], isFetching: loading, error } = useAccountQuery();

  // const disableSelect = !!bankAccount.accountId;
  const disabled = disabledForm;

  return (
    <Box>
      <InputWrapper
        label={'Chart of Accounts'}
        // description="With trust account Automated Trust Compliance, Seamless Bank Reconciliation, Real-time visibility into trust balances"
        sx={{
          flexDirection: 'column',
          display: 'flex',
        }}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
      >
        <SegmentedControl
          data={[
            {
              label: 'Create new account',
              value: 'create',
            },
            {
              label: 'Select existing account',
              value: 'select',
            },
          ]}
          value={coaType}
          disabled={disabled}
          onBlur={() => {}}
          onChange={(v) => {
            methods.setValue(`bankAccounts.${index}.accountId`, null);
            setCoaType(v as 'create' | 'select');
          }}
        />
      </InputWrapper>

      <Controller
        control={methods.control}
        name={`bankAccounts.${index}.accountId`}
        rules={{
          required: coaType === 'select' ? 'Please select an account' : false,
        }}
        render={({ field, fieldState }) => {
          const value =
            accounts?.find((x) => x.value === (field.value || '')) ?? null;

          return (
            <MantineCollapse in={coaType === 'select'}>
              <InputWrapper
                mt="sm"
                sx={{
                  flexDirection: 'column',
                  display: 'flex',
                }}
                error={fieldState.error?.message}
              >
                <InputSelect
                  value={value}
                  type="single"
                  inputProps={{
                    disabled: coaType === 'create' || disabled,
                    loadingQuery: loading,
                    placeholder: 'Select an account',
                    error: !!fieldState.error?.message,
                  }}
                  data={{
                    options: accounts,
                    error,
                    loading,
                  }}
                  setValue={(v) => field.onChange(v?.value ?? null)}
                />
              </InputWrapper>
            </MantineCollapse>
          );
        }}
      />
    </Box>
  );
};
